import { CardMediaPill } from '@app/components/base/CardMediaPill';
import { AppCardMediaOverlay } from '@app/components/base/app-card';
import { EnrichedCompositionListItemDto } from '@app/modules/compose/composition.types';
import { UploadedPlatformsStatus } from '@app/modules/social-export/components/UploadedPlatformsStatus';
import { getDisplayPlatformItems } from '@app/modules/social-export/lib/get-display-platform-items';
import { EditCardMediaPlaceholder } from '@app/modules/video-editions/components/edit-card/EditCardMediaPlaceholder';
import { EditCardThumbnail } from '@app/modules/video-editions/components/edit-card/EditCardThumbnail';
import { Box } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

import { CompositionCardEditStrip } from './CompositionCardEditStrip';
import { CompositionCardEditsCount } from './CompositionCardEditsCount';
import { useCompositionCardPreviewEdit } from './useCompositionCardPreviewEdit';

const composeListCardUnpublishedMessage = defineMessage({
  id: 'compose.listCard.unpublished',
  defaultMessage: 'Sin publicar',
});

/**
 * Top media for a composition list card: main 16:9 preview of the default (or hovered)
 * edit, with an overlaid strip of child-edit thumbnails when there is more than one.
 * Top-left shows the edits count; top-right pill shows social upload statuses or "Sin publicar".
 */
export function CompositionCardMedia({
  composition,
}: {
  composition: EnrichedCompositionListItemDto;
}) {
  const { formatMessage: $t } = useIntl();
  const { activeIndex, activeThumbnailSrc, handleStripItemEnter, handleStripLeave } =
    useCompositionCardPreviewEdit(composition.edits);
  const platformItems = getDisplayPlatformItems({
    uploadStatuses: composition.uploadStatuses,
  });
  const hasSocialStatuses = platformItems.length > 0;

  const baseMedia = activeThumbnailSrc ? (
    <EditCardThumbnail src={activeThumbnailSrc} />
  ) : (
    <EditCardMediaPlaceholder />
  );

  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <Box sx={{ position: 'relative', zIndex: 0, flexShrink: 0 }}>{baseMedia}</Box>
      <CompositionCardEditStrip
        edits={composition.edits}
        activeIndex={activeIndex}
        onItemEnter={handleStripItemEnter}
        onLeave={handleStripLeave}
      />
      <AppCardMediaOverlay overlayPosition="top-left">
        <CompositionCardEditsCount count={composition.edits.length} />
      </AppCardMediaOverlay>
      <AppCardMediaOverlay overlayPosition="top-right">
        <CardMediaPill>
          {hasSocialStatuses ? (
            <UploadedPlatformsStatus variant="pill" size="medium" items={platformItems} />
          ) : (
            $t(composeListCardUnpublishedMessage)
          )}
        </CardMediaPill>
      </AppCardMediaOverlay>
    </Box>
  );
}
